import type { RichTextContent } from "./types";

// Tags allowed in stored rich-text HTML. Anything outside this set is unwrapped
// (its markup removed) while keeping inner text content.
const ALLOWED_TAGS = new Set([
  "p", "br", "span", "div",
  "strong", "b", "em", "i", "u", "s", "strike", "del", "ins", "mark",
  "a", "ul", "ol", "li", "blockquote", "pre", "code",
  "h1", "h2", "h3", "h4", "h5", "h6", "sub", "sup",
  "img", "table", "thead", "tbody", "tfoot", "tr", "td", "th", "colgroup", "col",
]);

// Void/self-closing tags that never carry a closing counterpart.
const VOID_TAGS = new Set(["br", "img", "col"]);

const TAG_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "title", "target", "rel"]),
  img: new Set(["src", "alt", "title", "width", "height"]),
  li: new Set(["data-list", "data-checked", "class"]),
  ul: new Set(["data-checked", "class"]),
  ol: new Set(["data-checked", "class"]),
  td: new Set(["colspan", "rowspan", "class"]),
  th: new Set(["colspan", "rowspan", "class"]),
  span: new Set(["class", "style"]),
  p: new Set(["class", "style"]),
  div: new Set(["class", "style"]),
  pre: new Set(["class", "spellcheck"]),
  code: new Set(["class"]),
};

const GLOBAL_ALLOWED_ATTRS = new Set(["class"]);

function isSafeUrl(rawUrl: string, allowDataImage = false): boolean {
  const url = rawUrl.replace(/[\u0000-\u001F\u007F]/g, "").trim();

  if (!url) {
    return false;
  }

  if (allowDataImage && /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(url)) {
    return true;
  }

  if (/^(https?:|mailto:|tel:)/i.test(url)) {
    return true;
  }

  // Relative URLs and anchors.
  if (/^[#/.]/.test(url)) {
    return true;
  }

  // No scheme at all -> treat as relative.
  return !/^[a-z][a-z0-9+.-]*:/i.test(url);
}

function sanitizeStyle(style: string): string {
  const declarations: string[] = [];

  for (const declaration of style.split(";")) {
    const separatorIndex = declaration.indexOf(":");

    if (separatorIndex === -1) {
      continue;
    }

    const name = declaration.slice(0, separatorIndex).trim().toLowerCase();
    const value = declaration.slice(separatorIndex + 1).trim();

    if (!value) {
      continue;
    }

    const isColorProp = name === "color" || name === "background-color";
    const isSafeColor = /^(#[0-9a-f]{3,8}|rgba?\([0-9.,\s%]+\)|[a-z]+)$/i.test(value);

    if (isColorProp && isSafeColor) {
      declarations.push(`${name}: ${value}`);
    }
  }

  return declarations.join("; ");
}

function normalizeLinkHref(rawUrl: string): string | null {
  const url = rawUrl.replace(/[\u0000-\u001F\u007F]/g, "").trim();

  if (!url) {
    return null;
  }

  // In-page anchors and already-safe schemes pass through untouched.
  if (/^(https?:|mailto:|tel:)/i.test(url) || url.startsWith("#")) {
    return url;
  }

  // Any other explicit scheme (javascript:, data:, etc.) is unsafe.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    return null;
  }

  // Bare domains/paths typed by a user (e.g. "google.com") are external links,
  // not app-relative routes — force https so they don't resolve against the SPA.
  return `https://${url.replace(/^\/+/, "")}`;
}

function sanitizeAttributes(tagName: string, attrString: string): string {
  const allowed = TAG_ATTRS[tagName] ?? GLOBAL_ALLOWED_ATTRS;
  const attrPattern = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
  const result: string[] = [];

  let match: RegExpExecArray | null;

  while ((match = attrPattern.exec(attrString)) !== null) {
    const name = match[1]!.toLowerCase();
    const rawValue = match[3] ?? match[4] ?? match[5] ?? "";

    if (!allowed.has(name) || name.startsWith("on")) {
      continue;
    }

    if (name === "href" && tagName === "a") {
      const normalizedHref = normalizeLinkHref(rawValue);

      if (!normalizedHref) {
        continue;
      }

      result.push(`href="${normalizedHref.replace(/"/g, "&quot;")}"`);
      continue;
    }

    if (name === "src" && !isSafeUrl(rawValue, tagName === "img")) {
      continue;
    }

    if (name === "style") {
      const safeStyle = sanitizeStyle(rawValue);

      if (safeStyle) {
        result.push(`style="${safeStyle}"`);
      }

      continue;
    }

    if (name === "target") {
      result.push(`target="_blank"`);
      continue;
    }

    if (rawValue) {
      result.push(`${name}="${rawValue.replace(/"/g, "&quot;")}"`);
    } else {
      result.push(name);
    }
  }

  // Links always open in a new tab so they never hijack the single-page router.
  if (tagName === "a") {
    if (!result.some(attr => attr.startsWith("target="))) {
      result.push(`target="_blank"`);
    }

    if (!result.some(attr => attr.startsWith("rel="))) {
      result.push(`rel="noopener noreferrer"`);
    }
  }

  return result.length > 0 ? ` ${result.join(" ")}` : "";
}

/**
 * Allowlist-based HTML sanitizer that runs without a DOM, so it can be shared by
 * the browser editor and the Cloudflare Worker email renderer. Strips scripts,
 * styles, comments, event handlers, and unsafe URLs while keeping a curated set
 * of formatting tags and attributes.
 */
export function sanitizeRichTextHtml(value: unknown): string {
  if (typeof value !== "string" || !value) {
    return "";
  }

  let html = value
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, "")
    .replace(/<\s*style[^>]*>[\s\S]*?<\s*\/\s*style\s*>/gi, "")
    .replace(/<\s*(script|style)[^>]*\/?>/gi, "");

  html = html.replace(/<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b([^>]*?)(\/?)>/g, (_match, closing: string, rawName: string, attrs: string, selfClose: string) => {
    const tagName = rawName.toLowerCase();

    if (!ALLOWED_TAGS.has(tagName)) {
      return "";
    }

    if (closing === "/") {
      return VOID_TAGS.has(tagName) ? "" : `</${tagName}>`;
    }

    const safeAttrs = sanitizeAttributes(tagName, attrs);

    if (VOID_TAGS.has(tagName)) {
      return `<${tagName}${safeAttrs} />`;
    }

    return `<${tagName}${safeAttrs}${selfClose === "/" ? " /" : ""}>`;
  });

  return html.trim();
}

function decodeEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\u00a0/g, " ");
}

/**
 * Normalizes any stored value into the canonical `{ html }` rich-text shape.
 * Accepts an `{ html }` object or a raw HTML string. Legacy block-based content
 * is intentionally not supported and resolves to null.
 */
export function normalizeRichTextContent(value: unknown): RichTextContent {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    const html = sanitizeRichTextHtml(value);
    return html ? { html } : null;
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    const record = value as { html?: unknown };

    if (typeof record.html === "string") {
      const html = sanitizeRichTextHtml(record.html);
      return html ? { html } : null;
    }
  }

  return null;
}

/** Builds canonical rich-text content from raw editor HTML. */
export function createRichTextContent(html: unknown): RichTextContent {
  const sanitized = sanitizeRichTextHtml(html);
  return sanitized ? { html: sanitized } : null;
}

export type RichTextListMarker = "ordered" | "bullet" | "checked" | "unchecked";

export type RichTextListNode = {
  marker: RichTextListMarker;
  content: string;
  children: RichTextListNode[];
};

const QL_UI_SPAN = /<span\b[^>]*class="[^"]*\bql-ui\b[^"]*"[^>]*>[\s\S]*?<\/span>/gi;

function stripListItemContent(content: string): string {
  return content.replace(QL_UI_SPAN, "").trim();
}

/**
 * Quill stores lists flat: every item is a sibling `<li>` and nesting is encoded
 * as a `ql-indent-N` class rather than real `<ul>/<ol>` nesting. This parses the
 * inner HTML of one such flat list into a properly nested tree so both the
 * read-only renderer and the email renderer can emit native nested lists.
 */
export function parseRichTextListItems(inner: string): RichTextListNode[] {
  const flat = [...inner.matchAll(/<li\b([^>]*)>([\s\S]*?)<\/li>/gi)].map(match => {
    const attrs = match[1] ?? "";
    const indentMatch = /ql-indent-(\d+)/i.exec(attrs);
    const dataListMatch = /data-list\s*=\s*["']?([a-z]+)["']?/i.exec(attrs);
    const marker = (["ordered", "bullet", "checked", "unchecked"] as const).find(value => value === dataListMatch?.[1]) ?? "bullet";

    return {
      level: indentMatch ? Number(indentMatch[1]) : 0,
      node: { marker, content: stripListItemContent(match[2] ?? ""), children: [] } as RichTextListNode,
    };
  });

  const roots: RichTextListNode[] = [];
  const stack: { level: number; node: RichTextListNode }[] = [];

  for (const item of flat) {
    while (stack.length > 0 && stack[stack.length - 1]!.level >= item.level) {
      stack.pop();
    }

    const parent = stack[stack.length - 1];

    if (parent) {
      parent.node.children.push(item.node);
    } else {
      roots.push(item.node);
    }

    stack.push(item);
  }

  return roots;
}

function renderDisplayListNodes(nodes: RichTextListNode[]): string {
  let html = "";
  let index = 0;

  while (index < nodes.length) {
    const ordered = nodes[index]!.marker === "ordered";
    const tag = ordered ? "ol" : "ul";
    let body = "";

    while (index < nodes.length && (nodes[index]!.marker === "ordered") === ordered) {
      const node = nodes[index]!;
      const isCheck = node.marker === "checked" || node.marker === "unchecked";
      const glyph = node.marker === "checked" ? "\u2611 " : node.marker === "unchecked" ? "\u2610 " : "";
      const children = node.children.length > 0 ? renderDisplayListNodes(node.children) : "";

      body += `<li${isCheck ? ' class="rt-check"' : ""}>${glyph}${node.content}${children}</li>`;
      index += 1;
    }

    html += `<${tag}>${body}</${tag}>`;
  }

  return html;
}

/**
 * Rewrites Quill's flat indent-based lists into real nested `<ol>/<ul>` markup so
 * native list markers render correctly outside the editor (the editor itself
 * keeps the flat markup it needs).
 */
export function nestRichTextLists(html: string): string {
  if (!html) {
    return html;
  }

  return html.replace(/<(ol|ul)\b[^>]*>([\s\S]*?)<\/\1>/gi, (_match, _tag: string, inner: string) => {
    const nodes = parseRichTextListItems(inner);
    return nodes.length > 0 ? renderDisplayListNodes(nodes) : "";
  });
}


/** Extracts readable plain text from rich-text content for search and previews. */
export function getRichTextPlainText(value: unknown): string {
  const normalized = normalizeRichTextContent(value);

  if (!normalized?.html) {
    return "";
  }

  return decodeEntities(
    normalized.html
      .replace(/<\s*(br|li|p|div|tr|h[1-6]|blockquote|pre)\b[^>]*>/gi, "\n")
      .replace(/<\/\s*(li|p|div|tr|h[1-6]|blockquote|pre|ul|ol|table)\s*>/gi, "\n")
      .replace(/<[^>]*>/g, ""),
  )
    .replace(/[ \t]+/g, " ")
    .split("\n")
    .map(line => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** True when the rich-text content has no meaningful text or media. */
export function isRichTextEmpty(value: unknown): boolean {
  const normalized = normalizeRichTextContent(value);

  if (!normalized?.html) {
    return true;
  }

  if (/<img\b/i.test(normalized.html)) {
    return false;
  }

  return getRichTextPlainText(normalized).length === 0;
}
